/**
 * Product Gallery: the product page's main slide carousel and the thumbnail strip under it. One
 * script serves every product gallery (the CounterCool, Product Single and ACF Product Single
 * pages, and the Component Popup), so each gallery's markup carries the same `aura-gallery` attributes.
 * Embla adds no thumbnail, arrow or live region support, so each is wired here from its API. The
 * strip is a second carousel held on the main one's index, which is Embla's own thumbnail answer.
 * https://www.embla-carousel.com/docs/v8/examples/predefined#thumbnails
 *
 * Moving the gallery from another script: dispatch `aura-gallery:go-to` on the `[aura-gallery]`
 * element with `detail: { index }`, the zero-based slide position. The Product Single uses it to
 * show the image of the selected size.
 *
 * Live region: an element a screen reader reads out whenever its text changes, without the user
 * moving focus to it. https://developer.mozilla.org/en-US/docs/Web/Accessibility/ARIA/ARIA_Live_Regions
 */
(function () {
  // `keepSnaps` removes empty space at the start and end of a non-looping carousel while keeping
  // every slide a valid snap point. `dragFree` lets a flick carry along the row instead of settling
  // on the next thumbnail. Embla's own thumbnails example sets it.
  const THUMBS_OPTIONS = {
    containScroll: 'keepSnaps',
    dragFree: true,
  };

  const STARTED_ATTRIBUTE = 'data-gallery-started';

  // Each started gallery keeps its two carousels here, which is both the mark that it started and
  // the handle the Theme Editor's unload needs to stop them. A WeakMap holds no reference of its
  // own, so a removed gallery is still free to be collected.
  const startedGalleries = new WeakMap();

  // Each started gallery also keeps the controller of its `aura-gallery:go-to` listener here, so
  // stopping the gallery can remove that listener in one call.
  const goToControllers = new WeakMap();

  /**
   * Say each new slide's place in a live region, such as "Slide 2 of 8", for screen reader users.
   * @param {HTMLElement} galleryEl - The element holding the live region.
   * @param {object} carousel - The main carousel, already started.
   * @returns {void}
   */
  const startAnnouncer = (galleryEl, carousel) => {
    const statusEl = galleryEl.querySelector('[aura-gallery-status]');
    const positionText = (window.accessibilityStrings || {}).carouselSlidePosition;

    // Do not proceed if the live region or its sentence is missing. Calling `replace` on a missing
    // string would throw out of Embla's event loop.
    if (!statusEl || !positionText) {
      return;
    }

    // `select` fires on change and not at start, so nothing is read on page load.
    carousel.on('select', () => {
      statusEl.textContent = positionText
        .replace('[current]', carousel.selectedScrollSnap() + 1)
        .replace('[total]', carousel.scrollSnapList().length);
    });
  };

  /**
   * Start one gallery's main carousel, and its thumbnail strip when it has one, and keep the
   * arrows, thumbnails and live region in step.
   * @param {HTMLElement} galleryEl - The element holding the carousels.
   * @returns {void}
   */
  const startGallery = (galleryEl) => {
    const mainEl = galleryEl.querySelector('[aura-gallery-main]');
    const thumbsEl = galleryEl.querySelector('[aura-gallery-thumbs]');
    const previousArrowEl = galleryEl.querySelector('[aura-gallery-previous]');
    const nextArrowEl = galleryEl.querySelector('[aura-gallery-next]');

    const hasAllParts = mainEl && previousArrowEl && nextArrowEl;

    // Do not proceed if the main carousel or an arrow is missing. `EmblaCarousel` throws on a
    // missing element, and a one-slide gallery prints no arrows. The strip is optional: the
    // Component Popup gallery has none.
    if (!hasAllParts) {
      return;
    }

    // Do not proceed if this gallery already started. Two carousels would fight over one list.
    // The mark lives on the element and not in `startedGalleries`, because a page can print this
    // script's tag more than once (the product section and a popup both do), and each run of the
    // script has a `startedGalleries` of its own.
    if (galleryEl.hasAttribute(STARTED_ATTRIBUTE)) {
      return;
    }

    galleryEl.setAttribute(STARTED_ATTRIBUTE, '');

    // The main carousel takes Embla's defaults, which include `loop: false`: the arrows switch off
    // at either end instead of wrapping around.
    const mainCarousel = EmblaCarousel(mainEl);

    // Register the main carousel at once, so `stopGallery` can still destroy it if the strip fails
    // to start below.
    startedGalleries.set(galleryEl, [mainCarousel]);

    const thumbsCarousel = thumbsEl ? EmblaCarousel(thumbsEl, THUMBS_OPTIONS) : null;

    startedGalleries.set(
      galleryEl,
      [mainCarousel, thumbsCarousel].filter((carousel) => carousel),
    );

    // The thumbnail buttons are the strip's own slides, so Embla already holds them in order.
    const thumbEls = thumbsCarousel ? thumbsCarousel.slideNodes() : [];

    /**
     * Mark the thumbnail for the slide on screen and bring it into view.
     * @returns {void}
     */
    const updateThumbs = () => {
      // Do not proceed if the gallery has no strip. There is no thumbnail to mark.
      if (!thumbsCarousel) {
        return;
      }

      const currentIndex = mainCarousel.selectedScrollSnap();

      thumbsCarousel.scrollTo(currentIndex);

      // `aria-current` is the only mark of the on-screen thumbnail: screen readers read it and the
      // stylesheet draws the border from it. A boolean stringifies to the "true"/"false" it wants.
      thumbEls.forEach((thumbEl, index) => {
        thumbEl.setAttribute('aria-current', index === currentIndex);
      });
    };

    /**
     * Hide the slides that are off screen from screen readers, so only the current one is read.
     * @returns {void}
     */
    const updateSlides = () => {
      const currentIndex = mainCarousel.selectedScrollSnap();

      mainCarousel.slideNodes().forEach((slideEl, index) => {
        slideEl.setAttribute('aria-hidden', index !== currentIndex);
      });
    };

    const update = () => {
      window.AuraSliderHelpers.syncArrows(mainCarousel, previousArrowEl, nextArrowEl);
      updateThumbs();
      updateSlides();
    };

    previousArrowEl.addEventListener('click', () => mainCarousel.scrollPrev());
    nextArrowEl.addEventListener('click', () => mainCarousel.scrollNext());

    thumbEls.forEach((thumbEl, index) => {
      thumbEl.addEventListener('click', () => mainCarousel.scrollTo(index));
    });

    // `select` fires on a new slide, `reInit` when a resize changes the snaps. Run it here too so
    // the first slide starts marked and the previous arrow starts switched off.
    mainCarousel.on('select', update);
    mainCarousel.on('reInit', update);
    update();

    // Let another script move the gallery without holding the carousel itself.
    const goToController = new AbortController();

    goToControllers.set(galleryEl, goToController);

    galleryEl.addEventListener(
      'aura-gallery:go-to',
      (event) => mainCarousel.scrollTo(event.detail.index),
      { signal: goToController.signal },
    );

    startAnnouncer(galleryEl, mainCarousel);
  };

  /**
   * Stop one gallery's two carousels and let it be started again.
   * @param {HTMLElement} galleryEl - The element holding both carousels.
   * @returns {void}
   */
  const stopGallery = (galleryEl) => {
    const carousels = startedGalleries.get(galleryEl);

    // Do not proceed if this gallery never started. There is nothing to stop.
    if (!carousels) {
      return;
    }

    // `destroy` removes Embla's own resize and pointer listeners. Without it the Theme Editor
    // leaves one pair of carousels listening for every reload of the section.
    carousels.forEach((carousel) => carousel.destroy());
    startedGalleries.delete(galleryEl);
    galleryEl.removeAttribute(STARTED_ATTRIBUTE);

    // The listener may be missing when the strip failed to start, so ask first.
    const goToController = goToControllers.get(galleryEl);

    if (goToController) {
      goToController.abort();
      goToControllers.delete(galleryEl);
    }
  };

  // Both scripts are deferred and `layout/theme.liquid` prints `embla-carousel.js` first, so the
  // EmblaCarousel global is ready before this runs.
  document.addEventListener('DOMContentLoaded', () => {
    Array.from(document.querySelectorAll('[aura-gallery]')).forEach(startGallery);
  });

  // The Theme Editor replaces a section's HTML without firing `DOMContentLoaded` again, so start
  // the gallery in the replacement here.
  // https://shopify.dev/docs/storefronts/themes/best-practices/editor/integrate-sections-and-blocks
  document.addEventListener('shopify:section:load', (event) => {
    Array.from(event.target.querySelectorAll('[aura-gallery]')).forEach(startGallery);
  });

  // The editor fires this before it removes the old HTML, which is the only chance to stop the
  // carousels that HTML holds.
  document.addEventListener('shopify:section:unload', (event) => {
    Array.from(event.target.querySelectorAll('[aura-gallery]')).forEach(stopGallery);
  });
})();
